import { useLayoutEffect, useRef } from "react";
import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { usePlaybackStore } from "../state/playbackStore";
import { ChordDiagram } from "./ChordDiagram";
import { ChordName } from "./ChordName";
import { tempoChanges } from "../core/grid/fixedGrid";
import { useT } from "../i18n";
import { measureSegments } from "../core/grid/segments";
import { useChordView } from "../hooks/useChordView";
import { useStyledChart } from "../hooks/useStyledChart";
import { StyleChips } from "./StyleChips";
import { noteGlyphs } from "./keyText";

// Lead-sheet chord chart: measures laid out as a grid of tiles; a measure with a chord change is split
// into blocks. A single accent "playhead" tile slides (transform only) to the playing measure and fills
// beat by beat; played measures dim. Click any block to seek there. The play style chosen above the shape
// strip decides the fingerings and names (key/capo applied either way).
export function ChordGrid() {
  const c = useControllers();
  const t = useT();
  const chartMeasures = useProjectStore((s) => s.chartMeasures);
  const tempoGrid = useProjectStore((s) => s.tempoGrid);
  const currentMeasure = usePlaybackStore((s) => s.currentMeasure);
  const currentBeatTime = usePlaybackStore((s) => s.currentBeatTime);
  const gridRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const shown = useRef(false);
  const view = useChordView();   // key/capo applied: shapes and names move together
  const chart = useStyledChart(); // …and the play style on top

  // move the playhead onto the current measure's tile. Adjacent moves slide; a jump of more than one
  // row (seek) or the first appearance snaps, so it never sweeps across the whole sheet.
  useLayoutEffect(() => {
    const grid = gridRef.current, head = headRef.current;
    if (!grid || !head) return;
    const place = (animate: boolean) => {
      const cell = grid.querySelector<HTMLElement>(`[data-m="${currentMeasure}"]`);
      if (!cell) { head.style.opacity = "0"; shown.current = false; return; }
      const x = cell.offsetLeft, y = cell.offsetTop;
      const prevY = parseFloat(head.dataset.y ?? "NaN");
      const far = !Number.isFinite(prevY) || Math.abs(prevY - y) > cell.offsetHeight * 1.5;
      head.style.transition = animate && shown.current && !far ? "" : "none";
      head.style.width = cell.offsetWidth + "px";
      head.style.height = cell.offsetHeight + "px";
      head.style.transform = `translate(${x}px, ${y}px)`;
      head.style.opacity = "1";
      head.dataset.y = String(y);
      if (head.style.transition === "none") { void head.offsetWidth; head.style.transition = ""; }
      shown.current = true;
    };
    place(true);
    const ro = new ResizeObserver(() => place(false));
    ro.observe(grid);
    return () => ro.disconnect();
  }, [currentMeasure, chartMeasures]);

  // while playing, keep the playing measure inside the readable band (below the topbar, above the
  // floating play dock) — scroll the app's scroll area only when it leaves that band
  const isPlaying = usePlaybackStore((s) => s.isPlaying);
  useLayoutEffect(() => {
    if (!isPlaying || currentMeasure < 0) return;
    const cell = gridRef.current?.querySelector<HTMLElement>(`[data-m="${currentMeasure}"]`);
    const area = cell?.closest<HTMLElement>(".scroll-area");
    if (!cell || !area) return;
    const r = cell.getBoundingClientRect(), a = area.getBoundingClientRect();
    const top = a.top + 80, bottom = a.bottom - 150;
    if (r.top >= top && r.bottom <= bottom) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    area.scrollTo({ top: area.scrollTop + r.top - a.top - 140, behavior: reduce ? "auto" : "smooth" });
  }, [currentMeasure, isPlaying]);

  // beat-level fill inside the playhead: share of the measure's beats already reached
  const cm = chartMeasures[currentMeasure];
  let beatFrac = 0;
  if (cm && cm.beats.length) {
    let reached = 0;
    for (const b of cm.beats) if (currentBeatTime >= b.time - 0.02) reached++;
    beatFrac = reached / cm.beats.length;
  }
  useLayoutEffect(() => {
    if (fillRef.current) fillRef.current.style.transform = `scaleX(${beatFrac})`;
  }, [beatFrac]);

  // tempo-change markers (only when the song really changes tempo)
  const changes = tempoChanges(tempoGrid);
  const tempoAt = new Map(changes.length > 1 ? changes.map((c) => [c.bar, c.bpm] as const) : []);

  // distinct fingerings used in the song (chord view only)
  const diagrams = chart.diagrams;

  // fingering under the cursor (for the strip highlight)
  let nowKey: string | null = null;
  if (cm) {
    const segs = measureSegments(cm);
    let j = -1;
    for (let k = 0; k < segs.length; k++) { if (currentBeatTime >= segs[k].time - 0.02) j = k; else break; }
    if (j >= 0) nowKey = chart.at(currentMeasure, j)?.key ?? null;
  }

  // keep the highlighted shape centered in the horizontal strip (horizontal scroll only — never the page)
  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip || !nowKey) return;
    const el = strip.querySelector<HTMLElement>(`[data-key="${CSS.escape(nowKey)}"]`);
    if (el) strip.scrollTo({ left: el.offsetLeft - strip.clientWidth / 2 + el.offsetWidth / 2, behavior: "smooth" });
  }, [nowKey]);

  if (!chartMeasures.length) {
    return <div className="chord-grid-empty">{t("grid.empty")}</div>;
  }

  const seek = (time: number, measureIdx: number) => {
    c.activeSource().seek(time);
    usePlaybackStore.getState().setCurrentMeasure(measureIdx);
    usePlaybackStore.getState().setCurrentBeatTime(time);   // highlight the clicked block
  };

  return (
    <>
    <StyleChips chart={chart} />
    {diagrams.length > 0 && (
      <div className="chord-shapes" ref={stripRef} aria-label={t("grid.shapes")}>
        {diagrams.map((d) => (
          <ChordDiagram key={d.key} dataKey={d.key} frets={d.frets} label={d.name} added={d.added} marks={d.marks}
            from={d.from ? t("style.from", { c: noteGlyphs(d.from) }) : null} on={d.key === nowKey} />
        ))}
      </div>
    )}
    <div className="chord-grid" ref={gridRef}>
      <div className="cg-head" ref={headRef} aria-hidden="true"><div className="cg-fill" ref={fillRef} /></div>
      {chartMeasures.map((m, i) => {
        const segs = measureSegments(m);
        // the single active block = last segment whose start time has been reached by the cursor
        let activeSeg = -1;
        if (i === currentMeasure) {
          for (let j = 0; j < segs.length; j++) { if (currentBeatTime >= segs[j].time - 0.02) activeSeg = j; else break; }
        }
        const state = i === currentMeasure ? " on" : (currentMeasure >= 0 && i < currentMeasure ? " past" : "");
        return (
          <div key={i} data-m={i} className={"cg-cell" + state}>
            <span className="cg-num">{i + 1}</span>
            {tempoAt.has(i) && <span className="cg-tempo">♩={tempoAt.get(i)}</span>}
            <div className="cg-blocks">
              {segs.map((s, j) => {
                const active = j === activeSeg && !!s.chord;
                const shown = s.chord ? chart.at(i, j) : null;
                return (
                  <button key={j} className={"cg-block" + (active ? " on" : "") + (s.chord ? "" : " empty") + (segs.length > 2 ? " tight" : "")}
                    style={{ flexGrow: s.beats }} onClick={() => seek(s.time, i)} title={`${i + 1}마디`}>
                    {s.chord ? <ChordName chord={shown?.name ?? view.name(s.chord)} added={shown?.added} /> : "·"}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
    </>
  );
}
